import { useState, useRef, useCallback, useEffect } from "react";
import type { Message, MessageModality } from "../../shared/types";
import type { ApiClient } from "../api/client";
import { useVoiceInput } from "./useVoiceInput";
import { useVoiceOutput } from "./useVoiceOutput";
import type { MicPermission } from "./useMediaRecorder";

export interface UseConversationModeOptions {
  apiClient: ApiClient;
  messages: Message[];
  isLoading: boolean;
  sendMessage: (content: string, modality?: MessageModality) => Promise<void>;
}

export interface UseConversationModeResult {
  voiceInputState: ReturnType<typeof useVoiceInput>["voiceInputState"];
  isVoiceSupported: boolean;
  micPermission: MicPermission;
  playbackState: ReturnType<typeof useVoiceOutput>["playbackState"];
  playingMessageId: string | null;
  conversationModeEnabled: boolean;
  onExitPressed: () => void;
  stopAndSubmit: () => void;
  onMicPressed: () => void;
  onSpeakMessage: (messageId: string, text: string) => void;
  onUserSent: () => void;
}

export function useConversationMode({
  apiClient,
  messages,
  isLoading,
  sendMessage,
}: UseConversationModeOptions): UseConversationModeResult {
  const [conversationModeEnabled, setConversationModeEnabled] = useState(false);
  const conversationModeRef = useRef(false);

  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const detectedLanguageRef = useRef<string | undefined>(undefined);

  const onMicPermissionDenied = useCallback(() => {
    conversationModeRef.current = false;
    setConversationModeEnabled(false);
  }, []);

  const handleTranscript = useCallback(
    async (transcript: string, detectedLanguage?: string) => {
      if (!conversationModeRef.current) return;
      if (detectedLanguage) {
        detectedLanguageRef.current = detectedLanguage;
      }
      if (transcript.trim()) {
        await sendMessage(transcript, "Voice");
      }
    },
    [sendMessage],
  );

  const {
    voiceInputState,
    isVoiceSupported,
    micPermission,
    startRecording,
    stopAndSubmit,
    cancelRecording,
  } = useVoiceInput(apiClient, handleTranscript, detectedLanguageRef.current);

  const handlePlaybackEnded = useCallback(() => {
    if (conversationModeRef.current) {
      startRecording(onMicPermissionDenied);
    }
  }, [onMicPermissionDenied, startRecording]);

  const {
    playbackState,
    playingMessageId,
    stopPlayback,
    speakText,
    unlockAudio,
  } = useVoiceOutput(apiClient, handlePlaybackEnded);

  const lastSpokenIdRef = useRef<string | null>(
    messages[messages.length - 1]?.id ?? null,
  );

  useEffect(() => {
    if (!conversationModeRef.current) return;
    if (isLoading) return;

    const lastMessage = messages[messages.length - 1];
    if (!lastMessage) return;
    if (lastMessage.role !== "assistant") return;
    if (lastMessage.id === lastSpokenIdRef.current) return;
    if (lastMessage.error) return;
    if (!lastMessage.content.trim()) return;

    lastSpokenIdRef.current = lastMessage.id;

    speakText(lastMessage.content, lastMessage.id);
  }, [messages, conversationModeEnabled, isLoading, speakText]);

  const prevVoiceInputStateRef = useRef(voiceInputState);
  useEffect(() => {
    const prev = prevVoiceInputStateRef.current;
    prevVoiceInputStateRef.current = voiceInputState;
    if (
      prev === "error" &&
      voiceInputState === "idle" &&
      conversationModeRef.current
    ) {
      startRecording(onMicPermissionDenied);
    }
  }, [voiceInputState, onMicPermissionDenied, startRecording]);

  const enterConversationMode = useCallback(() => {
    unlockAudio();
    conversationModeRef.current = true;
    setConversationModeEnabled(true);
    const lastMessage = messagesRef.current[messagesRef.current.length - 1];
    if (lastMessage && lastMessage.role === "assistant") {
      lastSpokenIdRef.current = lastMessage.id;
    }
    startRecording(onMicPermissionDenied);
  }, [unlockAudio, onMicPermissionDenied, startRecording]);

  const exitConversationMode = useCallback(() => {
    conversationModeRef.current = false;
    setConversationModeEnabled(false);
    detectedLanguageRef.current = undefined;
    cancelRecording();
    stopPlayback();
  }, [cancelRecording, stopPlayback]);

  const onMicPressed = enterConversationMode;

  const onSpeakMessage = useCallback(
    (messageId: string, text: string) => {
      const lastMessageId =
        messagesRef.current[messagesRef.current.length - 1]?.id ?? null;
      if (
        playingMessageId === messageId &&
        (playbackState === "playing" || playbackState === "loading")
      ) {
        if (conversationModeRef.current && messageId === lastMessageId) {
          stopPlayback();
          enterConversationMode();
        } else {
          stopPlayback();
        }
      } else {
        cancelRecording();
        speakText(text, messageId);
      }
    },
    [
      playingMessageId,
      playbackState,
      stopPlayback,
      enterConversationMode,
      cancelRecording,
      speakText,
    ],
  );

  const onUserSent = exitConversationMode;
  const onExitPressed = exitConversationMode;

  return {
    voiceInputState,
    isVoiceSupported,
    micPermission,
    playbackState,
    playingMessageId,
    conversationModeEnabled,
    onExitPressed,
    stopAndSubmit,
    onMicPressed,
    onSpeakMessage,
    onUserSent,
  };
}
